import { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProfileSchema } from "../schema/profile.schema";
import type { ProfileFormData, ProfileData, UpdateProfilePayload } from "../types";
import { useUpdateProfile } from "../services/UpdateProfile";
import { useUpdateProfileImage } from "../services/UpdateImage";
import { useGetSectors } from "@services/sector.service";

const getDefaultFormValues = (data: ProfileData | undefined): ProfileFormData => ({
	// Business Details
	businessName: data?.businessDetails?.businessName || "",
	officeLandlineNumber: data?.businessDetails?.phoneNumber || "",
	websiteURL: data?.businessDetails?.businessWebsiteURL || "",
	principalLineOfBusiness: data?.businessDetails?.businessPrincipalLine || "",
	registeredOfficeAddress: data?.businessDetails?.address || "",
	// Use uuid for sector selection (to match signup form and dropdown options)
	sector: data?.businessDetails?.sector?.uuid || (data?.businessDetails?.sector?.id ? String(data.businessDetails.sector.id) : ""),
	// Contact Person Details
	personFullName: data?.businessDetails?.person?.fullName || "",
	personCnic: data?.businessDetails?.person?.cnic || "",
	personLandline: data?.businessDetails?.person?.landline || "",
	personMobile: data?.businessDetails?.person?.mobile || "",
	personEmail: data?.businessDetails?.person?.email || "",
	businessEmailAddress: data?.businessDetails?.businessEmailAddress || "",
	personAddress: data?.businessDetails?.person?.address || "",
	// KYC Details
	employerType: data?.businessDetails?.employerType || "",
	ntn: data?.businessDetails?.ntn || "",
});

// Flat payload structure matching API requirements
const transformFormDataToPayload = (formData: ProfileFormData): UpdateProfilePayload => ({
	cnic: formData.personCnic || undefined,
	businessName: formData.businessName || undefined,
	phoneNumber: formData.officeLandlineNumber || undefined,
	address: formData.registeredOfficeAddress || undefined,
	businessWebsiteURL: formData.websiteURL || undefined,
	businessPrincipalLine: formData.principalLineOfBusiness || undefined,
	businessEmailAddress: formData.businessEmailAddress || undefined,
	employerType: formData.employerType || undefined,
	ntn: formData.ntn || undefined,
	sectorId: formData.sector || undefined, // sector uuid for update
	personFullName: formData.personFullName || undefined,
	personEmailAddress: formData.personEmail || undefined,
	personMobileNumber: formData.personMobile || undefined,
	personLandlineNumber: formData.personLandline || undefined,
	personAddress: formData.personAddress || undefined,
});

export const useProfileForm = (data: ProfileData | undefined) => {
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile();
	const { mutate: updateProfileImage, isPending: isUploading } = useUpdateProfileImage();
	
	// Get sectors for dropdown
	const { sectors } = useGetSectors();
	const sectorOptions = useMemo(() => {
		if (!Array.isArray(sectors)) return [];
		// Use uuid as value to match signup form
		return sectors.map((sector: { uuid?: string; id?: string; label?: string; name?: string; value?: string }) => ({
			value: sector.uuid || sector.id || "",
			label: sector.label || sector.name || sector.value || "",
		}));
	}, [sectors]);

	// Employer type options - memoized
	const employerTypeOptions = useMemo(() => [
		{ value: "national", label: "National" },
		{ value: "international", label: "International" },
	], []);

	const profileSchema = useMemo(() => createProfileSchema(), []);
	
	const form = useForm<ProfileFormData>({
		resolver: zodResolver(profileSchema),
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues: getDefaultFormValues(data),
	});

	// Track if initial data has been set
	const initialDataSet = useRef(false);
	const prevDataRef = useRef<string | null>(null);

	// Reset form when data changes (only on actual data change, not on every render)
	useEffect(() => {
		if (data) {
			// Create a stable string representation to compare
			const dataString = JSON.stringify({
				businessName: data.businessDetails?.businessName,
				phoneNumber: data.businessDetails?.phoneNumber,
				sectorId: data.businessDetails?.sector?.id,
				personFullName: data.businessDetails?.person?.fullName,
			});
			
			// Only reset if data actually changed or first time
			if (!initialDataSet.current || prevDataRef.current !== dataString) {
				initialDataSet.current = true;
				prevDataRef.current = dataString;
				form.reset(getDefaultFormValues(data));
			}
		}
	}, [data]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];
		if (file) {
			updateProfileImage(file);
		}
	};

	const onSubmit = (formData: ProfileFormData): void => {
		console.log("Form submitted with data:", formData);

		const payload = transformFormDataToPayload(formData);
		console.log("Submitting profile update with payload:", payload);
		
		updateProfile(payload, {
			onSuccess: response => {
				console.log("Profile update successful:", response);
				setIsEditMode(false);
			},
		});
	};

	const handleCancel = (): void => {
		setIsEditMode(false);
		form.reset(getDefaultFormValues(data));
	};

	const handleEdit = (): void => {
		setIsEditMode(true);
		// Reset form to current data when entering edit mode
		form.reset(getDefaultFormValues(data));
	};

	return {
		isEditMode,
		isUpdating,
		isUploading,
		form,
		handleImageChange,
		onSubmit,
		handleCancel,
		handleEdit,
		sectorOptions,
		employerTypeOptions,
	};
};
